import { useState } from 'react'
import { cn } from 'lib/utils'
import {
    CompositeLocationAvatarProps,
    CompositeLocationAvatarLocation,
    SIZE_MAP,
} from './types'

/**
 * Square mosaic avatar representing a collection of locations.
 *
 * Layout by count:
 *   1 → single image fills square
 *   2 → two halves, side-by-side
 *   3 → one 50% left, two stacked right
 *   4 → 2×2 grid
 *   5+ → 2×2 grid, bottom-right cell shows "+N" overlay
 *
 * Missing / broken images fall back to the location's first initial
 * on a muted background.
 */
export function CompositeLocationAvatar({
    locations,
    size = 'md',
    className,
}: CompositeLocationAvatarProps) {
    const sizePx = typeof size === 'number' ? size : SIZE_MAP[size]
    const count = locations.length

    // Empty state — nothing to composite
    if (count === 0) {
        return (
            <div
                className={cn(
                    'flex items-center justify-center rounded-lg bg-muted text-muted-foreground text-xs font-medium',
                    className,
                )}
                style={{ width: sizePx, height: sizePx }}
                aria-label="No locations"
            >
                —
            </div>
        )
    }

    // 5+ locations — show first 3 + "+N" overlay in slot 4
    const overflow = count > 4 ? count - 3 : 0
    const displayed = overflow > 0 ? locations.slice(0, 3) : locations.slice(0, 4)

    return (
        <div
            className={cn(
                'relative overflow-hidden rounded-lg bg-muted',
                className,
            )}
            style={{ width: sizePx, height: sizePx }}
            role="img"
            aria-label={`${count} location${count === 1 ? '' : 's'}`}
        >
            <MosaicLayout
                locations={displayed}
                count={overflow > 0 ? 4 : count}
                overflow={overflow}
            />
        </div>
    )
}

type MosaicLayoutProps = {
    locations: CompositeLocationAvatarLocation[]
    count: 1 | 2 | 3 | 4 | number
    overflow: number
}

function MosaicLayout({ locations, count, overflow }: MosaicLayoutProps) {
    // Tailwind grid templates per count
    const gridClass =
        count === 1
            ? 'grid grid-cols-1 grid-rows-1'
            : count === 2
                ? 'grid grid-cols-2 grid-rows-1'
                : count === 3
                    ? 'grid grid-cols-2 grid-rows-2'
                    : 'grid grid-cols-2 grid-rows-2'

    return (
        <div className={cn(gridClass, 'h-full w-full gap-px')}>
            {locations.map((loc, i) => {
                // 3-location layout: first cell spans both rows on the left
                const spanClass =
                    count === 3 && i === 0 ? 'row-span-2' : ''

                const isOverflowSlot = overflow > 0 && i === 3

                return (
                    <Cell
                        key={`${loc.name}-${i}`}
                        location={loc}
                        className={spanClass}
                        overflowCount={isOverflowSlot ? overflow : 0}
                    />
                )
            })}

            {/* If overflow but only 3 displayed (shouldn't happen in current logic,
                kept for safety), render overflow as a standalone cell */}
            {overflow > 0 && locations.length === 3 && (
                <OverflowCell count={overflow} />
            )}
        </div>
    )
}

type CellProps = {
    location: CompositeLocationAvatarLocation
    className?: string
    overflowCount: number
}

function Cell({ location, className, overflowCount }: CellProps) {
    const [errored, setErrored] = useState(false)
    const showImage = location.imageUrl && !errored

    return (
        <div
            className={cn(
                'relative h-full w-full overflow-hidden bg-muted',
                className,
            )}
        >
            {showImage ? (
                <img
                    src={location.imageUrl!}
                    alt={location.name}
                    loading="lazy"
                    onError={() => setErrored(true)}
                    className="h-full w-full object-cover"
                />
            ) : (
                <div className="flex h-full w-full items-center justify-center text-muted-foreground text-xs font-medium">
                    {location.name.charAt(0).toUpperCase()}
                </div>
            )}

            {overflowCount > 0 && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/60 text-white text-xs font-semibold">
                    +{overflowCount}
                </div>
            )}
        </div>
    )
}

function OverflowCell({ count }: { count: number }) {
    return (
        <div className="flex h-full w-full items-center justify-center bg-black/60 text-white text-xs font-semibold">
            +{count}
        </div>
    )
}
